/**
 * Customers Page
 *
 * Displays list of all customers with:
 * - Search by name or email
 * - View customer details
 * - See assessment count per customer
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

export default function Customers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (user?.contractor_id) {
      fetchCustomers();
    }
  }, [user]);

  useEffect(() => {
    applySearch();
  }, [customers, searchTerm]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);

      // Fetch customers with assessment count
      const { data, error: fetchError } = await supabase
        .from('customers')
        .select(`
          *,
          assessments (count)
        `)
        .eq('contractor_id', user.contractor_id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setCustomers(data || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const applySearch = () => {
    if (!searchTerm) {
      setFilteredCustomers(customers);
      return;
    }

    const filtered = customers.filter(customer =>
      customer.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone?.includes(searchTerm)
    );

    setFilteredCustomers(filtered);
  };

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '40px' }}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading customers...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '40px' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ fontSize: '28px', marginBottom: '5px' }}>Customers</h1>
          <p style={{ color: '#7f8c8d', margin: 0 }}>
            View and manage all customers ({filteredCustomers.length} of {customers.length})
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            padding: '15px',
            background: '#fee2e2',
            border: '2px solid #ef4444',
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '20px'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Search */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
              Search Customers
            </label>
            <input
              type="text"
              className="input"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Customers List */}
        {filteredCustomers.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>👤</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50', marginBottom: '10px' }}>
              {searchTerm ? 'No matching customers' : 'No customers yet'}
            </div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              {searchTerm
                ? 'Try a different search term'
                : 'Customers will appear here after you create assessments'}
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '15px' }}>
            {filteredCustomers.map((customer) => {
              const assessmentCount = customer.assessments?.[0]?.count || 0;

              return (
                <div
                  key={customer.id}
                  onClick={() => navigate(`/customers/${customer.id}`)}
                  className="card"
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: '2px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div className="mobile-stack" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '20px', alignItems: 'center' }}>
                    {/* Customer Info */}
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50', marginBottom: '8px' }}>
                        {customer.full_name}
                      </div>

                      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '14px', color: '#64748b' }}>
                        {customer.email && (
                          <div>✉️ {customer.email}</div>
                        )}
                        {customer.phone && (
                          <div>📞 {customer.phone}</div>
                        )}
                      </div>

                      {customer.property_address && customer.property_address !== 'TBD' && (
                        <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '8px' }}>
                          📍 {customer.property_address}
                        </div>
                      )}

                      <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                        Added {new Date(customer.created_at).toLocaleDateString()}
                      </div>
                    </div>

                    {/* Assessment Count Badge */}
                    <div style={{
                      padding: '15px 25px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '12px',
                      textAlign: 'center',
                      color: 'white',
                      minWidth: '100px'
                    }}>
                      <div style={{ fontSize: '28px', fontWeight: '700', marginBottom: '4px' }}>
                        {assessmentCount}
                      </div>
                      <div style={{ fontSize: '12px', opacity: 0.9 }}>
                        Assessment{assessmentCount !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
